import {
  Button,
  Dropdown,
  DropdownItem,
  TextField,
} from '@cambridgeassessment/cambridge-ui';
import { BusinessStreams } from '@cambridgeassessment/ciam-commons';
import { Box, Chip, Typography } from '@material-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  businessUnits,
  COUNTRY_CODE_US,
  REPORT_MAKER_ROLES,
  Roles,
} from 'src/constants';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import useOrgAppRoleCollapse from 'src/hooks/useOrgAppRoleCollapse';
import useOrgAppRoles from 'src/hooks/useOrgAppRoles';
import useOrgSshAdmins from 'src/hooks/useOrgSshAdmins';
import { AppRoleInfo, NewOrgUser, RoleInfo } from 'src/types';
import CancelButtonDialog from '../CancelButtonDialog/CancelButtonDialog';
import OrgSshUserDialog from '../OrgSshUserDialog/OrgSshUserDialog';
import { rolesToPayload } from '../OrgUserRoles/utils';
import RolesExpandable from '../OrgUserRolesExpandableView/RolesExpandable';
import RolesSwitch from '../OrgUserRolesExpandableView/RolesSwitch';
import SearchField from '../SearchField/SearchField';
import SharedRoleConfirmationModal from './SharedRoleConfirmationModal';
import { useStyles } from './styles';

//NOSONAR FIXME: no space between asterisk on required Textfiels
//NOSONAR FIXME: no asterisk on required Dropdown
//NOSONAR FIXME: should be solved in CDS-608

//NOSONAR FIXME: issues dropdown on mobile size CDS-609

type Props = {
  onCancel: () => void;
  onSubmit: (
    data: NewOrgUser,
    callback: () => void,
    redirect: boolean,
    existingAdminsData?: { UID: string; roles: string[] }[]
  ) => void;
  isLoading: boolean;
};

type FormErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  roles?: string;
};

export const emailRegExp =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([A-Z\-0-9]+\.)+[A-Z]{2,}))$/i; // NOSONAR

export const creditorNumberRegExp = /^[a-zA-Z0-9@! ]*$/;

const AddOrgUserForm: React.FC<Props> = ({ onCancel, onSubmit, isLoading }) => {
  const history = useHistory();
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [creditorNumber, setCreditorNumber] = useState<string | undefined>('');
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<
    BusinessStreams[] | undefined
  >([]);
  const [error, setError] = useState({
    firstName: '',
    lastName: '',
    email: '',
    creditorNumber: '',
  });
  const [reportMakerCountryError, setReportMakerCountryError] =
    useState<boolean>(false);
  const [sharedRoles, setSharedRoles] = useState<string[]>([]);
  const [openSharedRoleConfirmationModal, setOpenSharedRoleConfirmationModal] =
    useState<boolean>(false);
  const [saveButtonClicked, setSaveButtonClicked] = useState<boolean>(false);
  const setContinueSaveButtonClicked = useState<boolean>(false)[1];
  const [showSshDialogError, setShowSshDialogError] = useState<boolean>(false);

  const { id, associateId } = useParams();
  const { filteredAppRoles, searchQuery, updateSearchQuery, updateExpand } =
    useOrgAppRoles(id);

  const isAssignedRole = (roleName: string) =>
    selectedRoles.indexOf(roleName) >= 0;
  const {
    user: { isCambridgeAssessor, country },
    isSSHUser,
    isDelegate,
    user,
  } = useAuth();

  const { addNotification } = useNotifications();
  const { admins } = useOrgSshAdmins(associateId);
  const [showSshDialog, setShowSshDialog] = useState(false);

  const { collapseAll, setCollapseAll } =
    useOrgAppRoleCollapse(filteredAppRoles);

  const handleRemoveBusinessUnits = useCallback(
    (businessUnit) => (): void =>
      setSelectedBusinessUnits((prev) =>
        prev.filter(
          (selectedBusinessUnit) => selectedBusinessUnit !== businessUnit
        )
      ),
    []
  );

  const validateFirstName = useCallback(
    (value) => {
      if (value.trim().length === 0) {
        setError({ ...error, firstName: 'First name is required' });
      } else if (value.length > 128) {
        setError({
          ...error,
          firstName: 'First name should not exceed 128 characters',
        });
      } else {
        setError({
          ...error,
          firstName: '',
        });
      }
    },
    [error]
  );

  const validateLastName = useCallback(
    (value) => {
      if (value.trim().length === 0) {
        setError({ ...error, lastName: 'Last name is required' });
      } else if (value.length > 128) {
        setError({
          ...error,
          lastName: 'Last name should not exceed 128 characters',
        });
      } else {
        setError({
          ...error,
          lastName: '',
        });
      }
    },
    [error]
  );

  const validateEmail = useCallback(
    (value) => {
      if (value.trim().length === 0) {
        setError({ ...error, email: 'Email is required' });
      } else if (!emailRegExp.test(value)) {
        setError({ ...error, email: 'Invalid email address' });
      } else if (value.length > 128) {
        setError({
          ...error,
          email: 'Email should not exceed 128 characters',
        });
      } else {
        setError({
          ...error,
          email: '',
        });
      }
    },
    [error]
  );

  const validateCreditorNumber = useCallback(
    (value) => {
      console.log('Validating creditor number:', value); // Debug log
      if (!creditorNumberRegExp.test(value)) {
        setError({
          ...error,
          creditorNumber:
            'Creditor number should contain letters, numbers, and @, ! only',
        });
      } else if (value.length > 64) {
        setError({
          ...error,
          creditorNumber: 'Creditor number should not exceed 64 characters',
        });
      } else {
        setError({
          ...error,
          creditorNumber: '',
        });
      }
    },
    [error]
  );

  const resetForm = useCallback(() => {
    setError({
      firstName: '',
      lastName: '',
      email: '',
      creditorNumber: '',
    });
    setFirstName('');
    setLastName('');
    setEmail('');
    setSelectedRoles([]);
    setCreditorNumber('');
    setSelectedBusinessUnits([]);
    setReportMakerCountryError(false);
  }, []);

  const handleSave = useCallback(
    async (
      redirect: boolean,
      existingAdminsData?: { UID: string; roles: string[] }[]
    ) => {
      onSubmit(
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email,
          roles: rolesToPayload(selectedRoles),
          creditorNumber: creditorNumber || undefined,
          businessStream: selectedBusinessUnits.length
            ? selectedBusinessUnits
            : undefined,
        },
        resetForm,
        redirect,
        existingAdminsData
      );
    },
    [
      creditorNumber,
      email,
      firstName,
      lastName,
      onSubmit,
      resetForm,
      selectedBusinessUnits,
      selectedRoles,
    ]
  );

  const handleSshProceed = async () => {
    setShowSshDialog(false);
    try {
      const loggedInUID = user.id;
      const existingAdmins = admins.filter((admin) =>
        admin.roles?.includes(Roles.SCHOOL_SUPPORT_HUB_COORDINATOR)
      );

      if (existingAdmins.some((admin) => admin.UID === loggedInUID)) {
        setShowSshDialogError(true);
        throw new Error();
      }

      const existingAdminsData = existingAdmins.map((admin) => ({
        UID: admin.UID,
        roles: admin.roles.filter(
          (role) => role !== Roles.SCHOOL_SUPPORT_HUB_COORDINATOR
        ),
      }));

      await handleSave(true, existingAdminsData);
    } catch (error) {
      addNotification({
        title: 'Error updating roles',
        variant: 'error',
      });
    }
  };

  const handleSubmit = useCallback(
    async (redirect = false) => {
      setOpenSharedRoleConfirmationModal(false);

      if (
        selectedRoles.includes(Roles.SCHOOL_SUPPORT_HUB_COORDINATOR) &&
        admins.length >= 1
      ) {
        setShowSshDialog(true);
        return;
      }

      if (
        REPORT_MAKER_ROLES.some((role) => selectedRoles.includes(role)) &&
        country !== COUNTRY_CODE_US
      ) {
        setReportMakerCountryError(true);
        return;
      }

      await handleSave(redirect);
    },
    [selectedRoles, admins, country, handleSave]
  );

  const handleCancel = () => {
    onCancel();
  };

  const sharedRole = (roleName: string) => {
    return []
      .concat(...filteredAppRoles.appRoles.map((ele) => ele.roles))
      .find((item) => item.isShared && item.name === roleName);
  };

  const getSharedRolesPlatforms = useMemo(() => {
    return filteredAppRoles.appRoles
      .filter((ele) => {
        return ele.roles.some((role) => sharedRoles.includes(role.name));
      })
      .map((appRole) => appRole.platform);
  }, [filteredAppRoles.appRoles, sharedRoles]);

  const getSharedRoleModalTitle = () => {
    return 'Add shared role?';
  };

  const getSharedRoleModalContent = () => {
    return {
      modalContent:
        'You have added a shared role. This role is used by multiple services, are you sure you want to continue?',
      platforms: getSharedRolesPlatforms,
    };
  };

  const openSharedConfirmationModal = (redirect: boolean) => {
    setOpenSharedRoleConfirmationModal(true);
    setContinueSaveButtonClicked(redirect);
  };

  const handleModalClose = () => {
    setOpenSharedRoleConfirmationModal(false);
    setSaveButtonClicked(false);
  };

  const handleToggle =
    (roleName: string, key: number) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;

      if (isChecked && !isAssignedRole(roleName)) {
        setSelectedRoles([...selectedRoles, roleName]);
        if (sharedRole(roleName)) {
          setSharedRoles([...sharedRoles, roleName]);
        }
      }

      if (!isChecked && isAssignedRole(roleName)) {
        setSelectedRoles(selectedRoles.filter((role) => role !== roleName));
        if (sharedRole(roleName)) {
          setSharedRoles(sharedRoles.filter((role) => role !== roleName));
        }
      }

      updateExpand(false, false, key);
    };

  const onCollapseAll = useCallback(() => {
    setCollapseAll((prev) => !prev);
    updateExpand(true, collapseAll, null);
  }, [updateExpand, collapseAll]);

  const isSaveDisabled = useMemo(() => {
    const hasErrors = Object.values(error).some((err) => err !== '');
    const hasRequiredFields = firstName && lastName && email;

    return isLoading || hasErrors || !hasRequiredFields;
  }, [error, firstName, lastName, email, isLoading]);

  const classes = useStyles();

  const handleRemoveRole = (role: string) => () => {
    // Implementation of role removal
    // Add your role removal logic here
  };

  return (
    <Box mt={4}>
      <Typography variant="h3" data-testid="form-title">
        User details
      </Typography>
      <Box mt={4} display="flex" flexWrap="wrap">
        <Box flexBasis={354} flexShrink={1} flexGrow={0} mr={[0, 3, 3]} mb={1}>
          <TextField
            required
            label="First or given name "
            validationType={error.firstName ? 'error' : undefined}
            helperText={error.firstName}
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              validateFirstName(e.target.value);
            }}
            data-testid="firstName-input"
          />
        </Box>
        <Box flexBasis={354} flexShrink={1} flexGrow={0} mr={[0, 0, 3]} mb={1}>
          <TextField
            required
            label="Last name or surname "
            validationType={error.lastName ? 'error' : undefined}
            helperText={error.lastName}
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              validateLastName(e.target.value);
            }}
            data-testid="lastName-input"
          />
        </Box>
      </Box>
      <Box display="flex" flexWrap="wrap">
        <Box flexBasis={354} flexShrink={1} flexGrow={0} mr={[0, 0, 3]} mb={1}>
          <TextField
            required
            label="Email address "
            validationType={error.email ? 'error' : undefined}
            helperText={error.email}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              validateEmail(e.target.value);
            }}
            data-testid="email-input"
          />
        </Box>
      </Box>
      {isCambridgeAssessor && (
        <>
          <Box display="flex" flexWrap="wrap">
            <Box
              flexBasis={354}
              flexShrink={1}
              flexGrow={0}
              mr={[0, 0, 3]}
              mb={1}
            >
              <TextField
                label="Creditor number"
                value={creditorNumber}
                onChange={(e) => {
                  setCreditorNumber(e.target.value);
                  validateCreditorNumber(e.target.value);
                }}
                data-testid="creditorNumber-input"
                validationType={error.creditorNumber ? 'error' : null}
                helperText={error.creditorNumber ? error.creditorNumber : null}
              />
            </Box>
          </Box>
          <Box display="flex" flexWrap="wrap">
            <Box flexBasis={354} flexGrow={0} mr={[0, 0, 3]}>
              <Dropdown
                placeholder="Please select one or more business units"
                label="Business unit"
                onChange={(_e, value) =>
                  setSelectedBusinessUnits(value as BusinessStreams[])
                }
                value={selectedBusinessUnits}
                multiple
                className={classes.dropdown}
                data-testid="businessUnit-input"
              >
                {businessUnits.map((businessUnit) => (
                  <DropdownItem key={businessUnit} value={businessUnit}>
                    {businessUnit}
                  </DropdownItem>
                ))}
              </Dropdown>
              {selectedBusinessUnits?.map((businessUnit) => (
                <Chip
                  key={businessUnit}
                  tabIndex={-1}
                  label={businessUnit}
                  className={classes.chip}
                  onDelete={handleRemoveBusinessUnits(businessUnit)}
                />
              ))}
            </Box>
          </Box>
        </>
      )}
      <Box display="flex">
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
            }}
          >
            <Typography
              data-testid="role-dropdown-text"
              className={classes.dropdown}
            >
              <Box component="label" className={classes.emphasizedText}>
                <Typography variant="h4" data-testid="user-roles-title">
                  Roles
                </Typography>
              </Box>
            </Typography>
            <Button
              onClick={() => onCollapseAll()}
              data-testid="role-collapse-button"
              style={{ backgroundColor: '#00588f' }}
            >
              {collapseAll ? 'Collapse All' : 'Expand All'}
            </Button>
          </Box>
          <Box mt={1}>
            <SearchField
              searchQuery={searchQuery}
              placeholder="Search for services or roles"
              onSubmit={updateSearchQuery}
              onChange={updateSearchQuery}
            />
          </Box>{' '}
          <Box>
            {(isDelegate
              ? filteredAppRoles.appRoles
              : isSSHUser
              ? filteredAppRoles.appRoles.filter(
                  (appRole: AppRoleInfo) =>
                    appRole.platform === 'School Support Hub'
                )
              : filteredAppRoles.appRoles
            ).map((appRole: AppRoleInfo, key) => (
              <Box mt={2} key={`accordion-box-${key}`}>
                <RolesExpandable
                  expand={appRole.expand}
                  keyId={key}
                  testid={`${appRole.platform}`}
                  summaryData={appRole.platform}
                  onExpand={updateExpand}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {appRole.roles.length === 0 ? (
                      <Typography
                        style={{ fontSize: '1.15em', lineHeight: '1.51em' }}
                      >
                        No roles for application
                      </Typography>
                    ) : (
                      appRole.roles.map((role: RoleInfo, keyId) => (
                        // eslint-disable-next-line react/jsx-key
                        <RolesSwitch
                          data-testid={`${appRole.platform}-${role.name}-rolesswitch`}
                          keyId={keyId}
                          testid={`${appRole.platform}-${role.name}`}
                          name={role.name}
                          alias={role.alias}
                          isShared={role.isShared}
                          description={role.description}
                          disabled={false}
                          isInternal={role.internal}
                          isRestricted={role.restricted}
                          checked={isAssignedRole(role.name)}
                          onToggle={handleToggle}
                        />
                      ))
                    )}
                  </Box>
                </RolesExpandable>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box mt={4} display="flex" flexWrap="wrap">
        <Box mr={2} mb={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (sharedRoles.length > 0) {
                openSharedConfirmationModal(true);
              } else {
                handleSubmit(true);
              }
            }}
            disabled={isSaveDisabled}
            data-testid="save-button"
          >
            Save
          </Button>
        </Box>
        <Box mb={2} className={classes.secondarySaveButton}>
          <Button
            variant="text"
            onClick={() => {
              if (sharedRoles.length > 0) {
                openSharedConfirmationModal(false);
              } else {
                handleSubmit(false);
              }
            }}
            disabled={isSaveDisabled}
            data-testid="save-and-add-button"
          >
            Save and add another
          </Button>
        </Box>
        <Box mr={2} mb={2}>
          <Button
            style={{ color: '#00588f' }}
            variant="text"
            onClick={handleCancel}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
        </Box>
      </Box>
      {reportMakerCountryError && (
        <CancelButtonDialog
          open={reportMakerCountryError}
          title={'Report Maker'}
          message={'Sorry, you cannot assign roles related to report maker'}
          onCancel={() => setReportMakerCountryError(false)}
        />
      )}
      {showSshDialog && (
        <OrgSshUserDialog
          open={showSshDialog}
          title="School Support Hub Coordinator Role"
          message="Note: Only one School Support Hub Coordinator Role can be assigned per centre. Proceeding will assign this role to the current user and remove it from any existing coordinator. Would you like to continue?"
          onCancel={() => setShowSshDialog(false)}
          onProceed={handleSshProceed}
          isError={false}
        />
      )}
      {showSshDialogError && (
        <OrgSshUserDialog
          open={showSshDialogError}
          title="School Support Hub Coordinator Role"
          message="Error: You are currently assigned as an SSH coordinator. Users cannot modify their own roles while logged in."
          onCancel={() => setShowSshDialogError(false)}
          onProceed={undefined}
          isError={true}
        />
      )}
      <SharedRoleConfirmationModal
        open={openSharedRoleConfirmationModal}
        title={getSharedRoleModalTitle()}
        content={getSharedRoleModalContent().modalContent}
        platforms={getSharedRolesPlatforms}
        closeModal={handleModalClose}
        onSave={handleSubmit}
      />
    </Box>
  );
};

export default AddOrgUserForm;
